import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin } from "lucide-react";
import WhatsAppIcon, { TikTokIcon } from "./WhatsAppIcon";
import { useSite } from "@/lib/site";

const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: TikTokIcon,
};

const SECTIONS = {
  ar: [
    ["#about", "من نحن"],
    ["#features", "المميزات"],
    ["#gallery", "التطبيق"],
    ["#owners", "لأصحاب الصالونات"],
    ["#faq", "الأسئلة الشائعة"],
    ["#contact", "تواصل معنا"],
  ],
  fr: [
    ["#about", "À propos"],
    ["#features", "Fonctionnalités"],
    ["#gallery", "Application"],
    ["#owners", "Salons"],
    ["#faq", "FAQ"],
    ["#contact", "Contact"],
  ],
};

export default function Footer() {
  const { tc, lang } = useSite();
  const f = tc?.footer;
  const c = tc?.contact;
  const year = new Date().getFullYear();
  const isFr = lang === "fr";

  return (
    <footer className="grain relative bg-forest-950 pb-10 pt-20 text-cream">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid gap-12 md:grid-cols-3">
          <div>
            <img src="/logo.jpg" alt="شعار حلاق - 7ala9" className="h-14 w-auto rounded-xl" data-testid="footer-logo" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-cream/60">{f?.about}</p>
            <div className="mt-6 flex items-center gap-3">
              {(f?.social || []).map((s, i) => {
                const Icon = SOCIAL_ICONS[s.platform] || Instagram;
                return (
                  <a
                    key={i}
                    href={s.url || "#"}
                    target={s.url && s.url !== "#" ? "_blank" : undefined}
                    rel="noreferrer"
                    aria-label={s.platform}
                    data-testid={`footer-social-${s.platform}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/15 text-cream/70 transition hover:border-gold hover:text-gold"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="font-head text-base font-bold text-gold">{isFr ? "Sections du site" : "أقسام الموقع"}</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {(SECTIONS[lang] || SECTIONS.ar).map(([href, label]) => (
                <li key={href}>
                  <a href={href} data-testid={`footer-link-${href.replace("#", "")}`} className="text-cream/70 transition hover:text-gold">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-head text-base font-bold text-gold">{isFr ? "Contactez-nous" : "تواصل معنا"}</h4>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-center gap-3" data-testid="footer-whatsapp-number">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/10 text-gold">
                  <WhatsAppIcon className="h-4 w-4" />
                </span>
                <span className="num-ltr text-cream/80">{c?.whatsappNumber}</span>
              </li>
              <li className="flex items-center gap-3" data-testid="footer-city">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/10 text-gold">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="text-cream/80">{f?.city}</span>
              </li>
              {f?.email && (
                <li className="flex items-center gap-3" data-testid="footer-email">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/10 text-gold">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span className="num-ltr text-cream/80">{f.email}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-cream/10 pt-6 text-xs text-cream/50 sm:flex-row">
          <p data-testid="footer-copyright">© {year} {isFr ? "حلاق - 7ala9. Tous droits réservés." : "حلاق - 7ala9. جميع الحقوق محفوظة."}</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition hover:text-gold">
              {f?.privacyTitle || "سياسة الخصوصية"}
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition hover:text-gold">
              {f?.termsTitle || "شروط الاستخدام"}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
